import { useCallback, useEffect, useRef, useState } from 'react';
const MIN_RATIO = 0.3;
const MAX_RATIO = 0.85;
const KEYBOARD_STEP = 0.02;
const clamp = (n) => Math.max(MIN_RATIO, Math.min(MAX_RATIO, n));
const loadRatio = (storageKey, fallback) => {
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored === null) {
            return fallback;
        }
        const parsed = Number(stored);
        return Number.isFinite(parsed) ? clamp(parsed) : fallback;
    }
    catch {
        // localStorage may be unavailable (private mode, blocked cookies, etc.).
        return fallback;
    }
};
const persistRatio = (storageKey, ratio) => {
    try {
        window.localStorage.setItem(storageKey, String(ratio));
    }
    catch {
        // Ignore — the in-memory ratio still drives the current session.
    }
};
// Horizontal resize controller: exposes the current ratio (0..1) of the left
// panel width relative to the container, plus drag/touch/keyboard handlers you
// attach to the resizer element. The chosen ratio persists in localStorage.
export const useResizableSplit = (defaultRatio, storageKey) => {
    const containerRef = useRef(null);
    const draggingRef = useRef(false);
    const [ratio, setRatio] = useState(() => loadRatio(storageKey, defaultRatio));
    useEffect(() => {
        persistRatio(storageKey, ratio);
    }, [ratio, storageKey]);
    useEffect(() => {
        const updateFromClientX = (clientX) => {
            if (!containerRef.current) {
                return;
            }
            const rect = containerRef.current.getBoundingClientRect();
            const next = (clientX - rect.left) / rect.width;
            setRatio(clamp(next));
        };
        const onMove = (e) => {
            if (!draggingRef.current) {
                return;
            }
            updateFromClientX(e.clientX);
        };
        const onTouchMove = (e) => {
            if (!draggingRef.current || e.touches.length === 0) {
                return;
            }
            updateFromClientX(e.touches[0].clientX);
        };
        const onEnd = () => {
            if (draggingRef.current) {
                draggingRef.current = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onEnd);
        window.addEventListener('touchmove', onTouchMove, { passive: true });
        window.addEventListener('touchend', onEnd);
        window.addEventListener('touchcancel', onEnd);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onEnd);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onEnd);
            window.removeEventListener('touchcancel', onEnd);
        };
    }, []);
    const onDragStart = useCallback(() => {
        draggingRef.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, []);
    const onTouchStart = useCallback(() => {
        draggingRef.current = true;
    }, []);
    const onKeyDown = useCallback((event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            setRatio(prev => clamp(prev - KEYBOARD_STEP));
        }
        else if (event.key === 'ArrowRight') {
            event.preventDefault();
            setRatio(prev => clamp(prev + KEYBOARD_STEP));
        }
        else if (event.key === 'Home') {
            event.preventDefault();
            setRatio(MIN_RATIO);
        }
        else if (event.key === 'End') {
            event.preventDefault();
            setRatio(MAX_RATIO);
        }
    }, []);
    return { ratio, containerRef, onDragStart, onTouchStart, onKeyDown };
};
